/**
 * Emissions Assets List Page
 *
 * Top-level Assets dashboard. KPIs, "Emissions by Asset Type" breakdown,
 * search box, server-paginated table of every asset in the org, and an
 * "Add Asset" CTA that drops the user into the asset wizard. Replaces
 * the deprecated admin Manage Assets page; row clicks deep-link to the
 * per-asset dashboard (`EMISSIONS_ASSET_DASHBOARD`).
 */

import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Plus, Search } from 'lucide-react';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import {
  useGetAssetsQuery,
  useGetAssetComparisonQuery,
  useGetEmissionsSummaryQuery,
} from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { FiscalYearSelector, getFiscalYearPeriod } from '../components/FiscalYearSelector';
import { AssetTypeDetailsList } from '../components/AssetBreakdownPanel';
import { AssetsTable } from '../components/assets/AssetsTable';

const PAGE_SIZE = 25;

export const EmissionsAssetsListPage = () => {
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();
  const history = useHistory();
  const { formatCompact } = useEmissionsFormatter();
  const { selectedFiscalYear, setSelectedFiscalYear, availableYears } = useEmissionsFilters();

  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(0);

  // Debounce the search input — typing each character would otherwise refire
  // the assets query on every keystroke and momentarily blank the table.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setPage(0);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const period = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);

  // Assets are scoped to the current level — no `includeChildren` toggle here.
  // Server-paginated so an org with thousands of assets stays responsive.
  const { data: assetsResponse, isLoading: isLoadingAssets } = useGetAssetsQuery(
    {
      initiativeId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(searchFilter ? { search: searchFilter } : {}),
    },
    { skip: !initiativeId },
  );
  const { data: comparisonData, isLoading: isLoadingComparison } = useGetAssetComparisonQuery(
    { initiativeId, periodStart: period.start, periodEnd: period.end },
    { skip: !initiativeId },
  );
  const { data: orgSummary } = useGetEmissionsSummaryQuery(
    { initiativeId, periodStart: period.start, periodEnd: period.end },
    { skip: !initiativeId },
  );

  const assets = useMemo(() => assetsResponse?.data ?? [], [assetsResponse?.data]);
  const total = assetsResponse?.pagination?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const comparisonAssets = useMemo(() => comparisonData?.assets ?? [], [comparisonData?.assets]);
  const orgTotalCo2e = orgSummary?.totalCO2e ?? 0;
  const assetTotalCo2e = comparisonData?.summary?.totalCo2e ?? 0;
  const assetPercentage = orgTotalCo2e > 0 ? (assetTotalCo2e / orgTotalCo2e) * 100 : 0;
  const unassignedCo2e = orgTotalCo2e - assetTotalCo2e;

  const typeDistribution = useMemo(() => {
    // KPIs and the type-distribution chart use the *full* org dataset (via
    // the comparison endpoint) so the totals don't shrink with search/page.
    const totals: Record<string, number> = {};
    for (const c of comparisonAssets) {
      totals[c.assetType] = (totals[c.assetType] ?? 0) + c.totalCo2e;
    }
    return Object.entries(totals)
      .map(([type, co2e]) => ({ type, co2e }))
      .sort((a, b) => b.co2e - a.co2e);
  }, [comparisonAssets]);

  const totalAssetCount = comparisonData?.summary?.totalAssets ?? comparisonAssets.length;
  const avgCo2ePerAsset = totalAssetCount > 0 ? assetTotalCo2e / totalAssetCount : 0;

  const navigateToAsset = (assetId: string) => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSET_DASHBOARD, { initiativeId, assetId }));
  };

  const navigateToAddAsset = () => {
    // The asset wizard is the same flow that powered the old admin "Add Asset"
    // button — `?method=from-inventory` jumps straight into the asset-method
    // step rather than the activity-method step.
    history.push(`${generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, { initiativeId })}?method=from-inventory`);
  };

  const isInitialLoading = isLoadingAssets && isLoadingComparison && page === 0 && !searchFilter;

  return (
    <Dashboard>
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Assets</h4>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            Operational assets and their emissions footprint.
          </p>
        </div>
        <div className='tw:flex tw:gap-2'>
          <FiscalYearSelector
            selectedYear={selectedFiscalYear}
            onYearChange={setSelectedFiscalYear}
            years={availableYears}
          />
          <Button onClick={navigateToAddAsset}>
            <Plus className='tw:size-3.5 tw:mr-1' />
            Add Asset
          </Button>
        </div>
      </div>

      {isInitialLoading ? (
        <div className='tw:text-center tw:py-16 tw:text-muted-foreground'>
          <Loader2 className='tw:size-8 tw:animate-spin tw:mx-auto tw:mb-3' />
          Loading assets...
        </div>
      ) : (
        <div className='tw:space-y-6'>
          <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-4'>
            <MetricCard
              label='Total Assets'
              value={String(totalAssetCount)}
              icon={<i className='fa fa-infinity tw:text-blue-600' />}
              iconBg='tw:bg-blue-500/10'
              borderColor='blue-500'
            />
            <MetricCard
              label='Asset-Linked CO₂e'
              value={formatCompact(assetTotalCo2e).formatted}
              unit={formatCompact(assetTotalCo2e).unit}
              subtext={
                unassignedCo2e > 0
                  ? `${formatCompact(unassignedCo2e).display} (${(100 - assetPercentage).toFixed(1)}%) unassigned`
                  : undefined
              }
              icon={<i className='fa fa-leaf tw:text-emerald-600' />}
              iconBg='tw:bg-emerald-500/10'
              borderColor='emerald-500'
            />
            <MetricCard
              label='% of Org Emissions'
              value={`${assetPercentage.toFixed(1)}%`}
              icon={<i className='fa fa-chart-pie tw:text-violet-600' />}
              iconBg='tw:bg-violet-500/10'
              borderColor='violet-500'
            />
            <MetricCard
              label='Avg CO₂e / Asset'
              value={totalAssetCount > 0 ? formatCompact(avgCo2ePerAsset).formatted : '—'}
              unit={totalAssetCount > 0 ? formatCompact(avgCo2ePerAsset).unit : undefined}
              icon={<i className='fa fa-calculator tw:text-orange-600' />}
              iconBg='tw:bg-orange-500/10'
              borderColor='orange-500'
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Emissions by Asset Type</CardTitle>
            </CardHeader>
            <CardContent>
              {typeDistribution.length === 0 ? (
                <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8 tw:mb-0'>
                  No asset data available for this period.
                </p>
              ) : (
                <AssetTypeDetailsList
                  typeDistribution={typeDistribution}
                  totalCo2e={assetTotalCo2e}
                  formatCompact={formatCompact}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className='tw:flex tw:items-center tw:justify-between tw:gap-3 tw:flex-wrap'>
                <CardTitle>All Assets</CardTitle>
                <div className='tw:relative tw:w-72 tw:max-w-full'>
                  <Search className='tw:absolute tw:left-2.5 tw:top-1/2 tw:-translate-y-1/2 tw:size-4 tw:text-muted-foreground' />
                  <input
                    type='text'
                    placeholder='Search assets...'
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className='tw:w-full tw:h-9 tw:rounded-md tw:border tw:border-border tw:bg-background tw:pl-8 tw:pr-3 tw:text-sm tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-ring'
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAssets ? (
                <div className='tw:text-center tw:py-8 tw:text-muted-foreground'>
                  <Loader2 className='tw:size-5 tw:animate-spin tw:mx-auto' />
                </div>
              ) : assets.length === 0 ? (
                <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8 tw:mb-0'>
                  {searchFilter ? `No assets match "${searchFilter}".` : 'No assets registered yet.'}
                </p>
              ) : (
                <AssetsTable
                  assets={assets}
                  comparison={comparisonAssets}
                  orgTotalCo2e={orgTotalCo2e}
                  onAssetClick={navigateToAsset}
                />
              )}

              {totalPages > 1 && (
                <div className='tw:flex tw:items-center tw:justify-between tw:mt-4 tw:text-sm tw:text-muted-foreground'>
                  <span>
                    {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className='tw:flex tw:gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className='tw:size-4' />
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className='tw:size-4' />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </Dashboard>
  );
};
